import { fetchCurrentUser } from '../../ducks/user.duck';
import { setInitialValues as setInitialValuesForPaymentMethods } from '../../ducks/paymentMethods.duck';
import { ensureStripeCustomer } from '../../util/data';
import {
  createFullPaymentSubscription,
  createSubscriptionWithIdVerification,
  getCaseDetails,
  getUserVerificationData,
  startIdentityVerification,
} from '../../util/api';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';

const getUserUuid = value => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  return value.uuid || value.id?.uuid || (typeof value.id === 'string' ? value.id : null);
};

// ================ Action types ================ //

export const SETUP_INTENT_REQUEST = 'app/BackGroundCheck/SETUP_INTENT_REQUEST';
export const SETUP_INTENT_SUCCESS = 'app/BackGroundCheck/SETUP_INTENT_SUCCESS';
export const SETUP_INTENT_ERROR = 'app/BackGroundCheck/SETUP_INTENT_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/BackGroundCheck/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/BackGroundCheck/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/BackGroundCheck/STRIPE_CUSTOMER_ERROR';

export const CREATE_SUBSCRIPTION_REQUEST = 'app/BackGroundCheck/CREATE_SUBSCRIPTION_REQUEST';
export const CREATE_SUBSCRIPTION_SUCCESS = 'app/BackGroundCheck/CREATE_SUBSCRIPTION_SUCCESS';
export const CREATE_SUBSCRIPTION_ERROR = 'app/BackGroundCheck/CREATE_SUBSCRIPTION_ERROR';

export const FETCH_VERIFICATION_DATA_REQUEST =
  'app/BackGroundCheck/FETCH_VERIFICATION_DATA_REQUEST';
export const FETCH_VERIFICATION_DATA_SUCCESS =
  'app/BackGroundCheck/FETCH_VERIFICATION_DATA_SUCCESS';
export const FETCH_VERIFICATION_DATA_ERROR = 'app/BackGroundCheck/FETCH_VERIFICATION_DATA_ERROR';

// ================ Reducer ================ //

const initialState = {
  setupIntentInProgress: false,
  setupIntentError: null,
  setupIntent: null,
  stripeCustomerFetched: false,
  createSubscriptionInProgress: false,
  createSubscriptionError: null,
  verificationData: null,
  verificationDataFetched: false,
  fetchVerificationDataInProgress: false,
  fetchVerificationDataError: null,
};

export default function backGroundCheckReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SETUP_INTENT_REQUEST:
      return { ...state, setupIntentInProgress: true, setupIntentError: null };
    case SETUP_INTENT_SUCCESS:
      return {
        ...state,
        setupIntentInProgress: false,
        setupIntentError: null,
        setupIntent: payload,
      };
    case SETUP_INTENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, setupIntentInProgress: false, setupIntentError: payload };
    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetched: true, stripeCustomerFetchError: payload };
    case CREATE_SUBSCRIPTION_REQUEST:
      return { ...state, createSubscriptionInProgress: true, createSubscriptionError: null };
    case CREATE_SUBSCRIPTION_SUCCESS:
      return { ...state, createSubscriptionInProgress: false, createSubscriptionError: null };
    case CREATE_SUBSCRIPTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, createSubscriptionInProgress: false, createSubscriptionError: payload };
    case FETCH_VERIFICATION_DATA_REQUEST:
      return {
        ...state,
        fetchVerificationDataInProgress: true,
        fetchVerificationDataError: null,
      };
    case FETCH_VERIFICATION_DATA_SUCCESS:
      return {
        ...state,
        fetchVerificationDataInProgress: false,
        verificationDataFetched: true,
        verificationData: payload,
      };
    case FETCH_VERIFICATION_DATA_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        fetchVerificationDataInProgress: false,
        verificationDataFetched: true,
        fetchVerificationDataError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setupIntentRequest = () => ({ type: SETUP_INTENT_REQUEST });
export const setupIntentSuccess = setupIntent => ({
  type: SETUP_INTENT_SUCCESS,
  payload: setupIntent,
});
export const setupIntentError = e => ({
  type: SETUP_INTENT_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const createSubscriptionRequest = () => ({ type: CREATE_SUBSCRIPTION_REQUEST });
export const createSubscriptionSuccess = () => ({ type: CREATE_SUBSCRIPTION_SUCCESS });
export const createSubscriptionError = e => ({
  type: CREATE_SUBSCRIPTION_ERROR,
  error: true,
  payload: e,
});

export const fetchVerificationDataRequest = () => ({ type: FETCH_VERIFICATION_DATA_REQUEST });
export const fetchVerificationDataSuccess = verificationData => ({
  type: FETCH_VERIFICATION_DATA_SUCCESS,
  payload: verificationData,
});
export const fetchVerificationDataError = e => ({
  type: FETCH_VERIFICATION_DATA_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const createStripeSetupIntent = () => (dispatch, getState, sdk) => {
  dispatch(setupIntentRequest());
  return sdk.stripeSetupIntents
    .create()
    .then(response => {
      const setupIntent = response.data.data;
      dispatch(setupIntentSuccess(setupIntent));
      return setupIntent;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'background-check-create-setup-intent-failed');
      dispatch(setupIntentError(error));
      throw error;
    });
};

export const stripeCustomer = () => dispatch => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(() => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'background-check-fetch-stripe-customer-failed');
      dispatch(stripeCustomerError(error));
    });
};

export const startIdVerification = params => (dispatch, getState) => {
  const { email, firstName, lastName, userName, country } = params || {};
  const stateCurrentUser = getState().user.currentUser;
  const currentUserId = getUserUuid(params?.currentUserId) || getUserUuid(stateCurrentUser);
  const verificationEmail = email;

  if (!currentUserId) {
    const error = storableError(new Error('currentUserId is required'));
    dispatch(createSubscriptionError(error));
    return Promise.reject(error);
  }

  return startIdentityVerification({
    email: verificationEmail,
    firstName,
    lastName,
    currentUserId,
    country,
  })
    .then(response => {
      const verificationId = response?.caseId;

      return getCaseDetails({
        verificationId,
        currentUserId,
        email: verificationEmail,
        firstName,
        lastName,
        name: userName,
      }).then(caseDetailsResponse => {
        const savedVerificationData = caseDetailsResponse?.savedVerificationData || null;
        dispatch(fetchVerificationDataSuccess(savedVerificationData));
        return caseDetailsResponse;
      });
    })
    .catch(e => {
      // The server tags country failures with a code (e.g. COUNTRY_NOT_SUPPORTED);
      // storableError drops unknown fields, so carry it over explicitly.
      const error = { ...storableError(e), code: e?.code || null };
      log.error(error, 'background-check-start-verification-failed');
      dispatch(createSubscriptionError(error));
      throw error;
    });
};

export const fetchUserVerificationData = currentUserId => (dispatch, getState) => {
  const stateCurrentUser = getState().user.currentUser;
  const userId = getUserUuid(currentUserId) || getUserUuid(stateCurrentUser);

  if (!userId) {
    dispatch(fetchVerificationDataSuccess(null));
    return Promise.resolve(null);
  }

  dispatch(fetchVerificationDataRequest());

  return getUserVerificationData({ currentUserId: userId })
    .then(response => {
      const verificationData = response?.verificationData || null;
      dispatch(fetchVerificationDataSuccess(verificationData));

      if (response?.isComplete) {
        return dispatch(fetchCurrentUser()).then(() => verificationData);
      }

      return verificationData;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'background-check-fetch-verification-data-failed');
      dispatch(fetchVerificationDataError(error));
      throw error;
    });
};

export const createBackgroundCheckSubscription = params => (dispatch, getState) => {
  dispatch(createSubscriptionRequest());
  const { stripeCustomerId, userEmail, userName, priceId } = params || {};
  const stateCurrentUser = getState().user.currentUser;
  const currentUserId = getUserUuid(params?.currentUserId) || getUserUuid(stateCurrentUser);
  const finalStripeCustomerId =
    stripeCustomerId ||
    ensureStripeCustomer(stateCurrentUser?.stripeCustomer).attributes.stripeCustomerId;

  if (!currentUserId) {
    const error = storableError(new Error('currentUserId is required'));
    dispatch(createSubscriptionError(error));
    return Promise.reject(error);
  }

  if (!finalStripeCustomerId) {
    const error = storableError(new Error('stripeCustomerId is required'));
    dispatch(createSubscriptionError(error));
    return Promise.reject(error);
  }

  return createSubscriptionWithIdVerification({
    stripeCustomerId: finalStripeCustomerId,
    currentUserId,
    userEmail, userName,
    priceId,
  })
    .then(response => {
      dispatch(createSubscriptionSuccess());
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'background-check-create-subscription-failed');
      dispatch(createSubscriptionError(error));
      throw error;
    });
};

// Used for invitees whose background check is bypassed but who still owe a real payment:
// charges the saved default payment method immediately instead of going through the
// default_incomplete + Certn-webhook flow (there's no webhook to finalize payment for them).
export const createFullPaymentBackgroundCheckSubscription = params => (dispatch, getState) => {
  dispatch(createSubscriptionRequest());
  const { stripeCustomerId, userEmail, userName, priceId } = params || {};
  const stateCurrentUser = getState().user.currentUser;
  const currentUserId = getUserUuid(params?.currentUserId) || getUserUuid(stateCurrentUser);
  const finalStripeCustomerId =
    stripeCustomerId ||
    ensureStripeCustomer(stateCurrentUser?.stripeCustomer).attributes.stripeCustomerId;

  if (!currentUserId) {
    const error = storableError(new Error('currentUserId is required'));
    dispatch(createSubscriptionError(error));
    return Promise.reject(error);
  }

  if (!finalStripeCustomerId) {
    const error = storableError(new Error('stripeCustomerId is required'));
    dispatch(createSubscriptionError(error));
    return Promise.reject(error);
  }

  if (!priceId) {
    const error = storableError(new Error('priceId is required'));
    dispatch(createSubscriptionError(error));
    return Promise.reject(error);
  }

  return createFullPaymentSubscription({
    stripeCustomerId: finalStripeCustomerId,
    currentUserId,
    userEmail,
    userName,
    priceId,
  })
    .then(response => {
      dispatch(createSubscriptionSuccess());
      return dispatch(fetchCurrentUser()).then(() => response);
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'background-check-create-full-payment-subscription-failed');
      dispatch(createSubscriptionError(error));
      throw error;
    });
};

export const loadData = () => (dispatch, getState) => {
  const { currentUser } = getState().user;
  dispatch(setInitialValuesForPaymentMethods());
  // dispatch(fetchUserVerificationData(currentUser?.id?.uuid))
  return dispatch(stripeCustomer());
};
